import { Issue, CanvasPreview, FeaturePRD } from "../types/issues";
import { LabelInterface } from "../types/labels";
import { Project } from "../types/issues";
import { Status } from "../types/status";
import { User } from "../types/users";
import { create } from "zustand";
import { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseStore } from "./zustand/supabaseStore";
import {
  setCanvasPreviews,
  deleteCanvas,
  supabaseSlice,
} from "@/store/supabaseSlice";
import store from "@/store/store";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";
import { addDays, isPast, isWithinInterval, startOfDay } from "date-fns";
import axios from "axios";

interface FilterOptions {
  status?: string[];
  assignee?: string[];
  priority?: number[];
  labels?: string[];
  project?: string[];
  dueDate?: {
    none?: boolean;
    overdue?: boolean;
    next7Days?: boolean;
    next30Days?: boolean;
  };
}

interface IssuesState {
  supabase: SupabaseClient | null;
  isInitialized: boolean;
  canvasPreviews: Record<string, CanvasPreview>;
  featurePrds: Record<string, FeaturePRD>;
  currentProject: Project | null;
  forceInitalize: () => void;
  setSupabase: (client: SupabaseClient) => void;
  initializeStore: (
    canvasPreviews: Record<string, CanvasPreview>,
    featurePrds: Record<string, any>,
    project: Project
  ) => void;
  getAllIssues: () => Issue[];
  addIssue: (issue: Issue) => void;
  updateIssue: (id: string, updatedIssue: Partial<Issue>) => void;
  deleteIssue: (id: string, sessionToken?: string) => Promise<void>;
  bulkDeleteIssues: (
    issueIds: string[],
    sessionToken?: string
  ) => Promise<void>;
  filterByStatus: (statusId: string) => Issue[];
  filterByPriority: (priority: number) => Issue[];
  filterByAssignee: (userId: string | null) => Issue[];
  filterByLabel: (labelId: string) => Issue[];
  filterByProject: (projectId: string) => Issue[];
  searchIssues: (query: string) => Issue[];
  filterIssues: (filters: FilterOptions) => Issue[];
  updateIssueStatus: (issueId: string, status: Status) => void;
  updateIssuePriority: (issueId: string, priority: number | null) => void;
  updateIssueAssignee: (issueId: string, assignee: User | null) => void;
  createIssueLabel: (label: LabelInterface) => void;
  updateIssueLabels: (issueId: string, labels: LabelInterface[]) => void;
  updateIssueEstimate: (issueId: string, estimate: number | null) => void;
  updateIssueProject: (issueId: string, project: Project | undefined) => void;
  getIssueById: (issueId: string) => Issue | undefined;
  updateIssueDueDate: (issueId: string, dueDate: Date | null) => Promise<void>;
  bulkUpdateIssueAssignee: (issueIds: string[], assignee: User | null) => void;
  bulkUpdateIssuePriority: (
    issueIds: string[],
    priority: number | null
  ) => void;
  bulkUpdateIssueStatus: (issueIds: string[], statusId: string) => void;
  bulkUpdateIssueEstimate: (
    issueIds: string[],
    estimate: number | null
  ) => void;
  bulkUpdateIssueLabel: (
    issueIdToLabels: { issueId: string; labels: LabelInterface[] }[]
  ) => void;
  bulkUpdateIssueDueDate: (issueIds: string[], dueDate: string | null) => void;
}

export const useIssuesStore = create<IssuesState>((set, get) => ({
  supabase: null,
  isInitialized: false,
  canvasPreviews: {},
  featurePrds: {},
  currentProject: null,
  forceInitalize: async () => {
    const state = get();
    if (!state.supabase || !state.currentProject) {
      return;
    }

    const canvasPreviewsQuery = state.supabase.rpc(
      "get_canvas_previews_filtered",
      {
        project_id_inp: state.currentProject?.id,
        env_inp: import.meta.env.VITE_ENVIRONMENT,
      }
    );

    const { data: canvasPreviewsData, error: canvasPreviewsError } =
      await canvasPreviewsQuery;

    if (canvasPreviewsError) {
      console.error("Error fetching canvas previews", canvasPreviewsError);
      return;
    }

    const featurePrdsQuery = state.supabase
      .from("feature_prd")
      .select("*")
      .in(
        "canvas_id",
        canvasPreviewsData.map((canvas) => canvas.id)
      );

    const { data: featurePrdsData, error: featurePrdsError } =
      await featurePrdsQuery;

    if (featurePrdsError) {
      console.error("Error fetching feature prds", featurePrdsError);
      return;
    }

    store.dispatch(setCanvasPreviews(canvasPreviewsData));

    const sbStoreState = useSupabaseStore.getState();
    sbStoreState.onDbSnapshot("featurePrds", {
      newItems: featurePrdsData,
      deletedItems: [],
    });

    // update the internal state
    set({
      canvasPreviews: Object.fromEntries(
        canvasPreviewsData.map((canvas) => [canvas.id, canvas])
      ),
      featurePrds: {
        ...state.featurePrds,
        ...Object.fromEntries(featurePrdsData.map((prd) => [prd.id, prd])),
      },
    });
  },
  setSupabase: (client: SupabaseClient) => {
    set({ supabase: client });
  },
  initializeStore: (
    canvasPreviews: Record<string, CanvasPreview>,
    featurePrds: Record<string, FeaturePRD>,
    project: Project
  ) => {
    if (!project || get().isInitialized) {
      return;
    }

    set({
      isInitialized: true,
      canvasPreviews,
      featurePrds,
      currentProject: project,
    });
  },
  getAllIssues: (): Issue[] => {
    const state = get();

    // check for project
    if (!state.currentProject) {
      return [];
    }

    const aggregatedIssues: Issue[] = [];
    Object.values(state.canvasPreviews).forEach((canvas) => {
      const prd = Object.values(state.featurePrds).find(
        (prd) => prd.canvas_id === canvas.id
      );

      aggregatedIssues.push(new Issue(canvas, prd));
    });

    return aggregatedIssues;
  },
  addIssue: (issue: Issue) => {
    const state = get();
    if (!state.currentProject) {
      return;
    }
  },
  updateIssue: (id: string, updatedIssue: Partial<Issue>) => {
    const state = get();
    if (!state.supabase) {
      return;
    }

    // TODO: Implement actual issue update in Supabase
    console.log("Issue update not implemented yet");
  },
  bulkUpdateIssueEstimate: (issueIds: string[], estimate: number | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    // convert issue IDs to feature prds
    const featurePrds = issueIds.map((issueId) => {
      const issue = state.getIssueById(issueId);
      if (!issue) {
        return null;
      }
      return issue.getRawFeaturePRD();
    });

    // filter out null values
    const validFeaturePrds = featurePrds.filter((prd) => prd !== null);

    // Update Supabase first
    state.supabase
      .from("feature_prd")
      .update({
        estimate: estimate,
      })
      .in(
        "id",
        validFeaturePrds.map((prd) => prd.id)
      )
      .select()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error updating estimate:", error);
          throw error;
        }

        if (!data) {
          console.error("No data returned from update");
          throw new Error("No data returned from update");
        }

        // Only update cached state after successful database update
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: data,
          deletedItems: [],
        });

        // update the internal state
        set({
          featurePrds: {
            ...state.featurePrds,
            ...Object.fromEntries(data.map((prd) => [prd.id, prd])),
          },
        });
      });
  },
  updateIssueEstimate: async (issueId: string, estimate: number | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    try {
      // Update Supabase first
      const { data, error } = await state.supabase
        .from("feature_prd")
        .update({
          estimate: estimate,
        })
        .eq("id", issueId)
        .select()
        .single();

      if (error) {
        console.error("Error updating estimate:", error);
        throw error;
      }

      if (!data) {
        console.error("No data returned from update");
        throw new Error("No data returned from update");
      }

      // Only update cached state after successful database update
      const sbStoreState = useSupabaseStore.getState();
      sbStoreState.onDbSnapshot("featurePrds", {
        newItems: [data],
        deletedItems: [],
      });

      // update the internal state
      set({
        featurePrds: {
          ...state.featurePrds,
          [data.id]: data,
        },
      });
    } catch (error) {
      console.error("Error in updateIssueEstimate:", error);
      throw error;
    }
  },
  bulkDeleteIssues: async (issueIds: string[], sessionToken?: string) => {
    const state = get();
    if (!state.supabase) {
      return;
    }

    try {
      // Soft delete the canvas by setting deleted_at
      const { error: updateError } = await state.supabase
        .from("canvases")
        .update({
          deleted_at: new Date().toISOString(),
        })
        .in("id", issueIds);

      if (updateError) {
        console.error("Error deleting issue:", updateError);
      }

      // Hard delete the associated feature PRD
      const { error: prdDeleteError } = await state.supabase
        .from("feature_prd")
        .delete()
        .in("canvas_id", issueIds);

      if (prdDeleteError) {
        console.error("Error deleting issue:", prdDeleteError);
      }

      // Update Redux store
      issueIds.forEach((id) => {
        store.dispatch(deleteCanvas(id));
        axios
          .delete(
            `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${id}/cache`,
            {
              headers: {
                Authorization: `Bearer ${sessionToken}`,
              },
            }
          )
          .catch((err) => {
            console.error("Error deleting canvas cache:", err.toString());
          });
      });

      // update the internal state
      set({
        canvasPreviews: Object.fromEntries(
          Object.entries(state.canvasPreviews).filter(
            ([key, canvas]) => !issueIds.includes(canvas.id)
          )
        ),
        featurePrds: Object.fromEntries(
          Object.entries(state.featurePrds).filter(
            ([key, prd]) => !issueIds.includes(prd.canvas_id ?? "")
          )
        ),
      });
    } catch (error) {
      console.error("Error deleting issue:", error);
    }
  },
  deleteIssue: async (id: string, sessionToken?: string) => {
    const state = get();
    if (!state.supabase) {
      return;
    }

    try {
      // Soft delete the canvas by setting deleted_at
      const { error: updateError } = await state.supabase
        .from("canvases")
        .update({
          deleted_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (updateError) {
        console.error("Error deleting issue:", updateError);
        return;
      }

      // Hard delete the associated feature PRD
      const { error: prdDeleteError } = await state.supabase
        .from("feature_prd")
        .delete()
        .eq("canvas_id", id);

      if (prdDeleteError) {
        console.error("Error deleting issue:", prdDeleteError);
        return;
      }

      // Update Redux store
      store.dispatch(deleteCanvas(id));

      axios
        .delete(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${id}/cache`,
          {
            headers: {
              Authorization: `Bearer ${sessionToken}`,
            },
          }
        )
        .catch((err) => {
          console.error("Error deleting canvas cache:", err.toString());
        });

      // update the internal state
      set({
        canvasPreviews: Object.fromEntries(
          Object.entries(state.canvasPreviews).filter(
            ([key, canvas]) => canvas.id !== id
          )
        ),
        featurePrds: Object.fromEntries(
          Object.entries(state.featurePrds).filter(
            ([key, prd]) => prd.canvas_id !== id
          )
        ),
      });
    } catch (error) {
      console.error("Error deleting issue:", error);
      return;
    }
  },
  filterByStatus: (statusId: string) => {
    return get()
      .getAllIssues()
      .filter((issue) => issue.status.id === statusId);
  },
  filterByPriority: (priority: number) => {
    return get()
      .getAllIssues()
      .filter((issue) => issue.priority === priority);
  },
  filterByAssignee: (userId: string | null) => {
    if (userId === null) {
      return get()
        .getAllIssues()
        .filter((issue) => issue.assignedUserId === null);
    }
    return get()
      .getAllIssues()
      .filter((issue) => issue.assignedUserId === userId);
  },
  filterByLabel: (labelId: string) => {
    return get()
      .getAllIssues()
      .filter((issue) => issue.labels.some((label) => label === labelId));
  },
  filterByProject: (projectId: string) => {
    return get()
      .getAllIssues()
      .filter((issue) => issue.projectId === projectId);
  },
  searchIssues: (query: string) => {
    const lowerCaseQuery = query.toLowerCase();
    return get()
      .getAllIssues()
      .filter(
        (issue) =>
          issue.title.toLowerCase().includes(lowerCaseQuery) ||
          issue.description.toLowerCase().includes(lowerCaseQuery)
      );
  },
  filterIssues: (filters: FilterOptions) => {
    console.log("filterIssues not implemented");

    return [];
  },
  bulkUpdateIssueStatus: (issueIds: string[], statusId: string) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    // convert issue IDs to feature prds
    const featurePrds = issueIds.map((issueId) => {
      const issue = state.getIssueById(issueId);
      if (!issue) {
        return null;
      }
      return issue.getRawFeaturePRD();
    });

    // filter out null values
    const validFeaturePrds = featurePrds.filter((prd) => prd !== null);

    // get status key
    const statusKey = Object.entries(KanbanDefaultStates).find(
      ([_, value]) => value.toLowerCase() === statusId.toLowerCase()
    )?.[0];

    if (!statusKey) {
      console.error("Could not find status key for", statusId);
      return;
    }

    // update supabase
    state.supabase
      .from("feature_prd")
      .update({
        status: KanbanDefaultStates[statusKey],
      })
      .in(
        "id",
        validFeaturePrds.map((prd) => prd.id)
      )
      .select()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error updating status:", error);
          throw error;
        }

        if (!data) {
          console.error("No data returned from update");
          throw new Error("No data returned from update");
        }

        // Only update cached state after successful database update
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: data,
          deletedItems: [],
        });

        // update the internal state
        set({
          featurePrds: {
            ...state.featurePrds,
            ...Object.fromEntries(data.map((prd) => [prd.id, prd])),
          },
        });
      });
  },
  updateIssueStatus: (issueId: string, status: Status) => {
    const state = get();
    const issue = state.getIssueById(issueId);
    if (!issue) {
      return;
    }

    // Find the feature PRD for this issue
    const featurePrd = issue.getRawFeaturePRD();

    if (!featurePrd) {
      return;
    }

    // Find the matching KanbanDefaultStates value
    const statusKey = Object.entries(KanbanDefaultStates).find(
      ([_, value]) => value.toLowerCase() === status.name.toLowerCase()
    )?.[0];

    if (!statusKey) {
      console.error("Could not find status key for", status.name);
      return;
    }

    // Create updated PRD with the exact enum value
    const updatedPrd = {
      ...featurePrd,
      status: KanbanDefaultStates[statusKey],
    };

    // Update Supabase
    state.supabase
      ?.from("feature_prd")
      .update({
        status: KanbanDefaultStates[statusKey],
      })
      .eq("id", featurePrd.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating status:", error);
          return;
        }

        // update the cached state
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: [updatedPrd],
          deletedItems: [],
        });

        // update the internal state
        set({
          featurePrds: {
            ...state.featurePrds,
            [featurePrd.id ?? ""]: updatedPrd,
          },
        });
      });
  },
  bulkUpdateIssuePriority: (issueIds: string[], priority: number | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    // convert issue IDs to feature prds
    const featurePrds = issueIds.map((issueId) => {
      const issue = state.getIssueById(issueId);
      if (!issue) {
        return null;
      }
      return issue.getRawFeaturePRD();
    });

    // filter out null values
    const validFeaturePrds = featurePrds.filter((prd) => prd !== null);

    // update supabase
    state.supabase
      .from("feature_prd")
      .update({
        priority: priority,
      })
      .in(
        "id",
        validFeaturePrds.map((prd) => prd.id)
      )
      .select()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error updating priority:", error);
          throw error;
        }

        if (!data) {
          console.error("No data returned from update");
          throw new Error("No data returned from update");
        }

        // Only update cached state after successful database update
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: data,
          deletedItems: [],
        });

        // update the internal state
        set({
          featurePrds: {
            ...state.featurePrds,
            ...Object.fromEntries(data.map((prd) => [prd.id, prd])),
          },
        });
      });
  },
  updateIssuePriority: (issueId: string, priority: number | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    // get feature prid id
    const issue = state.getIssueById(issueId);
    if (!issue) {
      return;
    }
    const featurePrd = issue.getRawFeaturePRD();
    if (!featurePrd) {
      return;
    }

    // Update Supabase first
    return state.supabase
      .from("feature_prd")
      .update({
        priority: priority !== null ? Number(priority) : null,
      })
      .eq("id", featurePrd.id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error updating priority:", error);
          throw error;
        }

        if (!data) {
          console.error("No data returned from update");
          throw new Error("No data returned from update");
        }

        // Only update cached state after successful database update
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: [data],
          deletedItems: [],
        });

        // update the internal state
        set({
          featurePrds: {
            ...state.featurePrds,
            [featurePrd.id ?? ""]: data,
          },
        });
      });
  },
  bulkUpdateIssueAssignee: (issueIds: string[], assignee: User | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    // Update Supabase first
    return state.supabase
      .from("canvases")
      .update({
        assigned_user_id: assignee?.id || null,
      })
      .in("id", issueIds)
      .select()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error updating assignee:", error);
          throw error;
        }

        if (!data) {
          throw new Error("No data returned from update");
        }

        store.dispatch(setCanvasPreviews(data));

        // update the internal state
        set({
          canvasPreviews: {
            ...state.canvasPreviews,
            ...Object.fromEntries(data.map((canvas) => [canvas.id, canvas])),
          },
        });
      });
  },
  updateIssueAssignee: (issueId: string, assignee: User | null) => {
    const state = get();
    const issues = state.getAllIssues();
    const issue = issues.find((i) => i.id === issueId);
    if (!issue) {
      return;
    }

    // Find the canvas for this issue
    const canvas = Object.values(state.canvasPreviews).find(
      (c) => c.id === issueId
    );
    if (!canvas) {
      return;
    }

    // Create updated canvas
    const updatedCanvas = {
      ...canvas,
      assigned_user_id: assignee?.id || null,
      last_updated: new Date().toISOString(),
    };

    // Update Supabase
    state.supabase
      ?.from("canvases")
      .update({
        assigned_user_id: assignee?.id || null,
      })
      .eq("id", updatedCanvas.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating assignee:", error);
          return;
        }

        // Update local state using Redux store
        store.dispatch(setCanvasPreviews([updatedCanvas]));

        // update the internal state
        set({
          canvasPreviews: {
            ...state.canvasPreviews,
            [updatedCanvas.id]: updatedCanvas,
          },
        });
      });
  },
  createIssueLabel: async (label: LabelInterface) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    if (!state.currentProject) {
      console.error("Current project not initialized");
      return;
    }

    try {
      // Label goes into the project table, in the labels column
      const formattedLabel = {
        id: label.id,
        name: label.label.trim(),
        label: label.label.trim(),
        color: {
          key: label.label.toLowerCase().replace(/\s+/g, "-"),
          label: label.label.trim(),
          value: label.color,
        },
      };

      const addLabelResponse = await state.supabase
        ?.from("projects")
        .update({
          labels: [...(state.currentProject?.labels || []), formattedLabel],
        })
        .eq("id", state.currentProject.id)
        .select()
        .single();

      if (!addLabelResponse || addLabelResponse.error) {
        return;
      }

      // Update Redux store with the new project data
      const updatedProject = addLabelResponse.data;
      store.dispatch(
        onProjectsSnapshot({
          newItems: [updatedProject],
          deletedItems: [],
        })
      );

      // update the internal state
      set({
        currentProject: updatedProject,
      });
    } catch (error) {}
  },
  bulkUpdateIssueLabel: (
    issueIdToLabels: { issueId: string; labels: LabelInterface[] }[]
  ) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    const updatePromises = issueIdToLabels.map((issueIdAndLabel) => {
      const issueId = issueIdAndLabel.issueId;
      const labelIDs = issueIdAndLabel.labels.map((label) => label.id);

      return state
        .supabase!.from("canvases")
        .update({ labels: labelIDs })
        .eq("id", issueId)
        .select()
        .single();
    });

    Promise.all(updatePromises)
      .then((results) => {
        const successfulUpdates = results
          .filter((result) => !result.error && result.data)
          .map((result) => result.data);

        if (successfulUpdates.length > 0) {
          store.dispatch(setCanvasPreviews(successfulUpdates));

          set({
            canvasPreviews: {
              ...state.canvasPreviews,
              ...Object.fromEntries(
                successfulUpdates.map((canvas) => [canvas.id, canvas])
              ),
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error in bulk update labels:", error);
      });
  },
  updateIssueLabels: async (issueId: string, labels: LabelInterface[]) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    const issues = state.getAllIssues();
    const issue = issues.find((i) => i.id === issueId);
    if (!issue) {
      return;
    }

    // Find the canvas for this issue
    const canvas = issue.getRawCanvas();
    if (!canvas) {
      return;
    }

    try {
      // Update Supabase first
      const response = await state.supabase
        ?.from("canvases")
        .update({
          labels: labels.map((label) => label.id),
        })
        .eq("id", canvas.id)
        .select()
        .single();

      if (!response || response.error) {
        throw new Error(response?.error?.message || "Failed to update labels");
      }

      // Only update local state once with the server response
      store.dispatch(setCanvasPreviews([response.data]));

      // update the internal state
      set({
        canvasPreviews: {
          ...state.canvasPreviews,
          [response.data.id]: response.data,
        },
      });
    } catch (error) {
      throw error;
    }
  },
  updateIssueProject: (issueId: string, project: Project | undefined) => {
    // TODO: Implement actual project update in Supabase
    console.log("Project update not implemented yet");
  },
  getIssueById: (issueId: string) => {
    return get()
      .getAllIssues()
      .find((issue) => issue.id === issueId);
  },
  bulkUpdateIssueDueDate: (issueIds: string[], dueDate: string | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    // fetch the feature prds
    const featurePrds = issueIds.map((issueId) => {
      const issue = state.getIssueById(issueId);
      if (!issue) {
        return null;
      }
      return issue.getRawFeaturePRD();
    });

    // filter out null values
    const validFeaturePrds = featurePrds.filter((prd) => prd !== null);

    state.supabase
      .from("feature_prd")
      .update({
        due_date: dueDate,
      })
      .in(
        "id",
        validFeaturePrds.map((prd) => prd.id)
      )
      .select()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error updating due date:", error);
          throw error;
        }

        if (!data) {
          console.error("No data returned from update");
          throw new Error("No data returned from update");
        }

        // Only update cached state after successful database update
        const sbStoreState = useSupabaseStore.getState();
        sbStoreState.onDbSnapshot("featurePrds", {
          newItems: data,
          deletedItems: [],
        });

        // update the internal state
        set({
          featurePrds: {
            ...state.featurePrds,
            ...Object.fromEntries(data.map((prd) => [prd.id, prd])),
          },
        });
      });
  },
  updateIssueDueDate: async (issueId: string, dueDate: Date | null) => {
    const state = get();
    if (!state.supabase) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.getIssueById(issueId);
    if (!issue) {
      return;
    }
    const featurePrd = issue.getRawFeaturePRD();
    if (!featurePrd) {
      return;
    }

    try {
      // Update Supabase first
      const { data, error } = await state.supabase
        .from("feature_prd")
        .update({
          due_date: dueDate?.toISOString() ?? null,
        })
        .eq("id", featurePrd.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating due date:", error);
        throw error;
      }

      if (!data) {
        console.error("No data returned from update");
        throw new Error("No data returned from update");
      }

      // Only update cached state after successful database update
      const sbStoreState = useSupabaseStore.getState();
      sbStoreState.onDbSnapshot("featurePrds", {
        newItems: [data],
        deletedItems: [],
      });

      // update the internal state
      set({
        featurePrds: {
          ...state.featurePrds,
          [data.id]: data,
        },
      });
    } catch (error) {
      console.error("Error in updateIssueDueDate:", error);
      throw error;
    }
  },
}));
